<template>
    <div v-if="visible" class="pb2-empty">
        <div class="pb2-empty__card">
            <Icon icon="phosphor/regular/squares-four" class="pb2-empty__icon size-5" aria-hidden="true" />
            <h3 class="pb2-empty__title">{{ t('empty_title') }}</h3>
            <p class="pb2-empty__hint">{{ t('empty_hint') }}</p>
            <div v-if="templates.length" class="pb2-empty__templates">
                <button
                    v-for="template in templates"
                    :key="template.key"
                    type="button"
                    class="pb2-empty__template"
                    :dusk="`pb2-template-${template.key}`"
                    @click="insert(template)"
                >
                    {{ template.label }}
                </button>
            </div>
            <button v-else-if="panels" type="button" class="pb2-empty__palette" @click="panels.open('palette')">
                {{ t('empty_open_palette') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBuilderHost, useBuilderT } from '@src/Common/Builder/host';
import type { BlockTemplate } from '@src/Common/Builder/host';
import type { BlockNode } from '@src/Common/Builder/types';
import { useBuilderRegistry } from '@src/Common/Builder/useBuilderRegistry';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { useCanvasChromeOptional } from '@src/Common/Builder/useCanvasChrome';
import { useDragMachine } from '@src/Common/Builder/useDragMachine';
import { usePanelManagerOptional } from '@src/Common/Builder/usePanelManager';

const t = useBuilderT();
const host = useBuilderHost();
const store = useBuilderStore();
const registry = useBuilderRegistry();
const drag = useDragMachine();
const panels = usePanelManagerOptional();
const chrome = useCanvasChromeOptional();

const visible = computed(
    () =>
        store.content.value.length === 0 &&
        store.mode.value === 'edit' &&
        registry.loaded.value &&
        !drag.dragging.value &&
        (chrome?.state.value ?? 'ready') === 'ready',
);

const slugsOf = (blocks: BlockNode[]): string[] =>
    blocks.flatMap((block) => [String(block.type), ...slugsOf(block.children ?? [])]);

/** Only templates whose every type resolves in this host's registry. */
const templates = computed(() =>
    (host.templates ?? []).filter((template) =>
        slugsOf(template.blocks).every((slug) => registry.typeOf(slug) !== null),
    ),
);

const insert = (template: BlockTemplate): void => {
    store.pasteAt(template.blocks, null, 0, null);
};
</script>

<style scoped>
.pb2-empty {
    position: absolute;
    inset: 0;
    z-index: 25;
    display: grid;
    place-items: center;
    padding: 1.5rem;
    pointer-events: none;
}

.pb2-empty__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    max-width: 320px;
    padding: 1.25rem 1.5rem;
    text-align: center;
    background: var(--pb2-surface-raised, #fff);
    border: 1px dashed color-mix(in srgb, var(--pb2-muted, #a1a1aa) 45%, transparent);
    border-radius: var(--pb2-radius, 6px);
    pointer-events: auto;
}

.pb2-empty__icon {
    color: color-mix(in srgb, var(--pb2-muted, #a1a1aa) 80%, transparent);
}

.pb2-empty__title {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--pb2-text, #3f3f46);
}

.pb2-empty__hint {
    margin: 0;
    font-size: 0.71875rem;
    line-height: 1.45;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-empty__templates {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.375rem;
    margin-top: 0.625rem;
}

.pb2-empty__template,
.pb2-empty__palette {
    height: 26px;
    padding: 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--pb2-text, #3f3f46);
    background: color-mix(in srgb, var(--pb2-border, #e4e4e7) 45%, transparent);
    border: 0;
    border-radius: 5px;
    cursor: pointer;
    transition:
        color 140ms ease,
        background-color 140ms ease;
}

.pb2-empty__template:hover,
.pb2-empty__palette:hover {
    color: var(--pb2-accent-contrast, #fff);
    background: var(--pb2-accent, #2563eb);
}

.pb2-empty__palette {
    margin-top: 0.625rem;
}

@media (prefers-reduced-motion: reduce) {
    .pb2-empty__template,
    .pb2-empty__palette {
        transition: none;
    }
}
</style>
